// Customer-facing verification page — upload document + status.
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, CheckCircle2, Clock, AlertCircle, XCircle, ArrowLeft, ShieldCheck } from "lucide-react";
import { customerApi } from "@/lib/customerAuth";

const DOC_LABELS = {
    jamaican_drivers_license: "Jamaican driver's licence",
    jamaican_voter_id: "Jamaican voter ID",
    jamaican_passport: "Jamaican passport",
    jamaican_national_id: "Jamaican national ID",
    utility_bill: "Recent utility bill (JA address)",
    bank_statement: "Recent bank statement (JA address)",
    government_correspondence: "Government correspondence (JA address)",
    other: "Other (staff will review)",
};

const STATUS_UI = {
    not_submitted: { color: "bg-amber-100 text-amber-800", icon: AlertCircle, label: "Action required" },
    pending_review: { color: "bg-blue-100 text-blue-800", icon: Clock, label: "Under review" },
    approved: { color: "bg-jade/15 text-jade", icon: CheckCircle2, label: "Approved" },
    rejected: { color: "bg-red-100 text-red-700", icon: XCircle, label: "Not verified" },
    info_required: { color: "bg-amber-100 text-amber-800", icon: AlertCircle, label: "More info needed" },
    expired: { color: "bg-neutral-200 text-neutral-600", icon: XCircle, label: "Deadline expired" },
};

function fmtDeadline(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
function hoursUntil(iso) {
    if (!iso) return 0;
    return Math.max(0, Math.round((new Date(iso) - new Date()) / 3600000));
}

export default function AccountVerification() {
    const { reservationId } = useParams();
    const nav = useNavigate();
    const [config, setConfig] = useState(null);
    const [existing, setExisting] = useState(null);
    const [reservation, setReservation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ full_legal_name: "", ja_address: "", doc_type: "jamaican_drivers_license", consent: false });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploadedPath, setUploadedPath] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: cfg }, resRes, verRes] = await Promise.all([
                customerApi.get("/resident-rate/config"),
                customerApi.get(`/customer/reservations/${reservationId}`),
                customerApi.get(`/verifications/by-reservation/${reservationId}`),
            ]);
            setConfig(cfg);
            setReservation(resRes.data);
            setExisting(verRes.data?.id ? verRes.data : null);
            if (resRes.data?.guest_name && !form.full_legal_name) {
                setForm((f) => ({ ...f, full_legal_name: resRes.data.guest_name }));
            }
        } catch {
            toast.error("Could not load verification");
        } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservationId]);

    useEffect(() => { load(); }, [load]);

    const uploadFile = async () => {
        if (!file) return null;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await customerApi.post("/verifications/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setUploadedPath(data.path);
            toast.success("Document uploaded — you can submit now");
            return data.path;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
            return null;
        } finally { setBusy(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) return toast.error("Please confirm your information is accurate");
        let path = uploadedPath;
        if (!path) path = await uploadFile();
        if (!path) return;
        setBusy(true);
        try {
            await customerApi.post("/verifications", {
                reservation_id: reservationId,
                full_legal_name: form.full_legal_name.trim(),
                ja_address: form.ja_address.trim(),
                doc_type: form.doc_type,
                consent: form.consent,
                document_path: path,
            });
            toast.success("Submitted — we'll email you the moment it's reviewed");
            await load();
            setFile(null);
            setUploadedPath(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not submit");
        } finally { setBusy(false); }
    };

    if (loading) return <div className="text-navy/60 text-sm">Loading verification…</div>;

    const status = existing?.status || "not_submitted";
    const S = STATUS_UI[status] || STATUS_UI.not_submitted;
    const showForm = ["not_submitted", "rejected", "info_required", "expired"].includes(status);
    const hoursLeft = existing?.deadline ? hoursUntil(existing.deadline) : (config?.deadline_hours || 24);

    return (
        <div className="space-y-6" data-testid="account-verification">
            <Link to="/account/bookings" className="text-sm text-gold hover:underline"><ArrowLeft size={13} className="inline mr-1" />Back to bookings</Link>

            <div className="bg-white border border-navy/10 rounded-2xl p-8">
                <div className="text-[10px] uppercase tracking-[0.22em] text-jade flex items-center gap-2"><ShieldCheck size={12} /> Jamaican Resident Rate verification</div>
                <h1 className="font-display text-3xl md:text-4xl text-navy mt-2">{reservation?.room_name || "Your reservation"}</h1>
                <div className="text-navy/60 text-sm mt-1">{reservation?.check_in} → {reservation?.check_out} · Confirmation {reservation?.confirmation_number}</div>

                <div className="mt-6 flex items-start gap-3 flex-wrap">
                    <div className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full ${S.color}`}>
                        <S.icon size={13} /> {S.label}
                    </div>
                    {existing?.deadline && status !== "approved" && status !== "expired" && (
                        <div className="text-[11px] uppercase tracking-[0.14em] text-navy/60">
                            Deadline: {fmtDeadline(existing.deadline)} · {hoursLeft} hours remaining
                        </div>
                    )}
                </div>

                {status === "approved" && (
                    <div className="mt-6 rounded-xl bg-jade/5 border border-jade/30 p-5">
                        <p className="text-sm text-navy">Thank you — your resident rate is confirmed. Please bring matching identification to check-in.</p>
                    </div>
                )}
                {status === "rejected" && (
                    <div className="mt-6 rounded-xl bg-red-50 border border-red-200 p-5">
                        <p className="text-sm text-navy">We were unable to verify your eligibility. Your reservation is maintained at the standard rate. If a payment difference is owed, our team will email you a secure link.</p>
                        {existing?.decision_reason && <p className="text-[12px] text-navy/60 mt-2 italic">Reviewer note: {existing.decision_reason}</p>}
                    </div>
                )}
                {status === "info_required" && (
                    <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-5">
                        <p className="text-sm text-navy">We need a little more information to complete your verification:</p>
                        <p className="text-sm text-navy/80 italic mt-2">{existing?.decision_reason}</p>
                        <p className="text-[11px] text-navy/60 mt-3">Please resubmit below before the deadline.</p>
                    </div>
                )}

                {/* Privacy notice */}
                {showForm && (
                    <div className="mt-6 rounded-xl border border-navy/10 bg-navy/[0.03] p-5 text-[12px] text-navy/70 leading-relaxed">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-navy/60 mb-2">Privacy notice</div>
                        <p>We collect your document solely to verify Jamaican residency and prevent misuse of the resident rate. It is stored privately, viewed only by authorised Serenity Cove staff, and retained for the duration of your stay plus 90 days. Questions? <a href="mailto:stay@serenitycoveresorts.com" className="text-gold hover:underline">stay@serenitycoveresorts.com</a>.</p>
                    </div>
                )}

                {showForm && (
                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Full legal name (as it appears on your document)</span>
                            <input type="text" required data-testid="ver-name" value={form.full_legal_name} onChange={(e) => setForm({ ...form, full_legal_name: e.target.value })}
                                className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Jamaican residential address</span>
                            <textarea required rows={2} data-testid="ver-address" value={form.ja_address} onChange={(e) => setForm({ ...form, ja_address: e.target.value })}
                                className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Document type</span>
                            <select required data-testid="ver-doctype" value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
                                className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm">
                                {(config?.accepted_doc_types || Object.keys(DOC_LABELS)).map((k) => (
                                    <option key={k} value={k}>{DOC_LABELS[k] || k}</option>
                                ))}
                            </select>
                        </label>

                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Upload document (JPG, PNG, WEBP, HEIC or PDF · max 12 MB)</span>
                            <input
                                type="file"
                                required={!uploadedPath}
                                accept="image/*,.pdf,.heic,.heif"
                                capture="environment"
                                onChange={(e) => { setFile(e.target.files?.[0] || null); setUploadedPath(null); }}
                                data-testid="ver-file"
                                className="mt-2 block w-full text-sm text-navy file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[11px] file:uppercase file:tracking-[0.16em] file:bg-navy file:text-ivory hover:file:bg-navy/90"
                            />
                            {file && !uploadedPath && (
                                <button type="button" onClick={uploadFile} disabled={busy} data-testid="ver-upload" className="mt-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] px-4 py-2 border border-navy/25 rounded-full text-navy hover:bg-navy hover:text-ivory">
                                    <Upload size={13} /> {busy ? "Uploading…" : "Upload document"}
                                </button>
                            )}
                            {uploadedPath && <div className="mt-2 text-[11px] text-jade inline-flex items-center gap-1.5"><CheckCircle2 size={12} /> Document uploaded — ready to submit</div>}
                        </label>

                        <label className="flex items-start gap-3 text-[12px] text-navy/70 cursor-pointer">
                            <input type="checkbox" required data-testid="ver-consent" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-navy" />
                            <span>I confirm this information is accurate and I am the primary guest on this reservation. I agree to Serenity Cove's Jamaican Resident Rate terms.</span>
                        </label>

                        <div className="flex items-center justify-between pt-3 border-t border-navy/10">
                            <button type="button" onClick={() => nav(-1)} className="text-sm text-navy/60 hover:text-navy">Cancel</button>
                            <button type="submit" disabled={busy} data-testid="ver-submit" className="inline-flex items-center gap-2 bg-jade text-ivory px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] hover:bg-jade/90 disabled:opacity-60">
                                <ShieldCheck size={13} /> {busy ? "Submitting…" : "Submit for verification"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
